import { ConsigneeOrganType } from '../ConsignedStep/interfaces';

export interface IFgtsMargin {
  id?: string;
  consult_date_time?: string;
  document?: string;
  available_fgts_guarantee_balance?: number;
  severance_fine_fgts_guarantee?: number;
  message?: string;
}

export type TLoanGuaranteeInfo = {
  has_guarantees?: boolean;
  available_rescission_allowance_percent?: number;
  available_fgts_guarantee_balance?: number;
  severance_fine_fgts_guarantee?: number;
  guarantee_balance_fgts_id?: string;
};

export const mergeLoanGuaranteeInfoFromFgtsBalance = (
  current: TLoanGuaranteeInfo | undefined,
  fgtsMargin: IFgtsMargin,
): TLoanGuaranteeInfo => ({
  ...current,
  available_fgts_guarantee_balance: fgtsMargin.available_fgts_guarantee_balance,
  severance_fine_fgts_guarantee: fgtsMargin.severance_fine_fgts_guarantee,
  guarantee_balance_fgts_id:
    fgtsMargin.id ?? current?.guarantee_balance_fgts_id,
});

export const mergeLoanGuaranteeInfoFromConsignedGuaranteeInfo = (
  current: TLoanGuaranteeInfo | undefined,
  guaranteeInfo?: {
    has_guarantees?: boolean;
    available_rescission_allowance_percent?: number;
  },
): TLoanGuaranteeInfo => ({
  ...current,
  has_guarantees: guaranteeInfo?.has_guarantees,
  available_rescission_allowance_percent:
    guaranteeInfo?.available_rescission_allowance_percent,
});

export type TLoanGuaranteeFormValues = {
  guarantee_balance_fgts_id?: string;
  hasRescissionPenalty: boolean;
  rescissionPenaltyPercent: string;
  hasFgtsBalanceGuarantee: boolean;
  fgtsBalanceAmount: string;
  hasFgtsRescissionPenalty: boolean;
  fgtsRescissionPenaltyAmount: string;
};

export type TApplicationGuaranteeInfoPayload = {
  guarantee_balance_fgts_id?: string;
  available_fgts_guarantee_balance?: number;
  severance_fine_fgts_guarantee?: number;
  available_rescission_allowance_percent?: number;
};

export const buildGuaranteeInfoPayload = (
  loanGuaranteeInfo?: TLoanGuaranteeInfo,
  formValues?: TLoanGuaranteeFormValues,
): TApplicationGuaranteeInfoPayload | undefined => {
  if (!formValues) {
    return undefined;
  }

  const hasAnyGuaranteeSelected =
    formValues.hasRescissionPenalty ||
    formValues.hasFgtsBalanceGuarantee ||
    formValues.hasFgtsRescissionPenalty;

  if (!hasAnyGuaranteeSelected) {
    return undefined;
  }

  const payload: TApplicationGuaranteeInfoPayload = {};

  if (formValues.hasFgtsBalanceGuarantee && formValues.fgtsBalanceAmount) {
    payload.available_fgts_guarantee_balance =
      Number(formValues.fgtsBalanceAmount.replace(/\D/g, '')) / 100;
  }

  if (
    formValues.hasFgtsRescissionPenalty &&
    formValues.fgtsRescissionPenaltyAmount
  ) {
    payload.severance_fine_fgts_guarantee =
      Number(formValues.fgtsRescissionPenaltyAmount.replace(/\D/g, '')) / 100;
  }

  if (formValues.hasRescissionPenalty && formValues.rescissionPenaltyPercent) {
    payload.available_rescission_allowance_percent = parseFloat(
      formValues.rescissionPenaltyPercent.replace(',', '.'),
    );
  }

  const requiresFgtsBalanceId =
    payload.available_fgts_guarantee_balance != null ||
    payload.severance_fine_fgts_guarantee != null;

  if (requiresFgtsBalanceId) {
    const guaranteeBalanceFgtsId =
      formValues.guarantee_balance_fgts_id ||
      loanGuaranteeInfo?.guarantee_balance_fgts_id;

    if (guaranteeBalanceFgtsId) {
      payload.guarantee_balance_fgts_id = guaranteeBalanceFgtsId;
    }
  }

  if (
    !payload.guarantee_balance_fgts_id &&
    payload.available_fgts_guarantee_balance == null &&
    payload.severance_fine_fgts_guarantee == null &&
    payload.available_rescission_allowance_percent == null
  ) {
    return undefined;
  }

  return payload;
};

export const defaultLoanGuaranteeFormValues: TLoanGuaranteeFormValues = {
  guarantee_balance_fgts_id: '',
  hasRescissionPenalty: false,
  rescissionPenaltyPercent: '',
  hasFgtsBalanceGuarantee: false,
  fgtsBalanceAmount: '',
  hasFgtsRescissionPenalty: false,
  fgtsRescissionPenaltyAmount: '',
};

const parseGuaranteeCurrencyValue = (value?: string): number => {
  if (!value) {
    return 0;
  }

  return Number(value.replace(/\D/g, '')) / 100;
};

const roundToTwoDecimals = (value: number): number =>
  Math.round(value * 100) / 100;

export const shouldValidateFgtsGuaranteeFinancedRatio = (
  formValues: TLoanGuaranteeFormValues,
): boolean =>
  formValues.hasFgtsBalanceGuarantee || formValues.hasFgtsRescissionPenalty;

export const getFgtsGuaranteeAmountSum = (
  formValues: TLoanGuaranteeFormValues,
): number => {
  let sum = 0;

  if (formValues.hasFgtsBalanceGuarantee && formValues.fgtsBalanceAmount) {
    sum += parseGuaranteeCurrencyValue(formValues.fgtsBalanceAmount);
  }

  if (
    formValues.hasFgtsRescissionPenalty &&
    formValues.fgtsRescissionPenaltyAmount
  ) {
    sum += parseGuaranteeCurrencyValue(formValues.fgtsRescissionPenaltyAmount);
  }

  return roundToTwoDecimals(sum);
};

export const hasFgtsGuaranteeFieldsSelected = (
  formValues: TLoanGuaranteeFormValues,
): boolean =>
  formValues.hasFgtsBalanceGuarantee || formValues.hasFgtsRescissionPenalty;

export const getMarginWithFgtsCoverageAmount = (
  availableBalance: number,
  formValues: TLoanGuaranteeFormValues,
): number =>
  roundToTwoDecimals(availableBalance + getFgtsGuaranteeAmountSum(formValues));

export const getMarginWithFgtsCoverageRatio = (
  availableBalance: number,
  formValues: TLoanGuaranteeFormValues,
  financedAmount: number,
): number =>
  roundToTwoDecimals(
    getMarginWithFgtsCoverageAmount(availableBalance, formValues) /
      financedAmount,
  );

const MARGIN_MIN_LOAN_RATIO = 0.5;

export const getFgtsGuaranteeExpectedFinancedAmount = (
  financedAmount?: number,
): number => roundToTwoDecimals((financedAmount ?? 0) * 0.5);

export const isExactFinancedCoverageAmountValid = (
  coverageAmount: number,
  financedAmount?: number,
): boolean =>
  roundToTwoDecimals(coverageAmount) ===
  getFgtsGuaranteeExpectedFinancedAmount(financedAmount);

export const getMarginWithFgtsExpectedGuaranteeAmount = (
  financedAmount?: number,
  availableBalance?: number,
): number =>
  roundToTwoDecimals(
    getFgtsGuaranteeExpectedFinancedAmount(financedAmount) -
      (availableBalance ?? 0),
  );

export const shouldUseMarginWithFgtsCoverageValidation = (
  formValues: TLoanGuaranteeFormValues,
): boolean =>
  formValues.hasRescissionPenalty && hasFgtsGuaranteeFieldsSelected(formValues);

export const isFgtsGuaranteeFinancedRatioValid = (
  formValues: TLoanGuaranteeFormValues,
  financedAmount?: number,
  isFgtsQueryEnabled = false,
  availableBalance?: number,
): boolean => {
  if (!isFgtsQueryEnabled) {
    return true;
  }

  if (shouldUseMarginWithFgtsCoverageValidation(formValues)) {
    return isRescissionMarginWithFgtsCoverageValid(
      availableBalance,
      formValues,
      financedAmount,
    );
  }

  if (!shouldValidateFgtsGuaranteeFinancedRatio(formValues)) {
    return true;
  }

  if (!financedAmount) {
    return true;
  }

  return isExactFinancedCoverageAmountValid(
    getFgtsGuaranteeAmountSum(formValues),
    financedAmount,
  );
};

export const isRescissionMarginWithFgtsCoverageValid = (
  availableBalance: number | undefined,
  formValues: TLoanGuaranteeFormValues,
  financedAmount?: number,
): boolean => {
  if (!shouldUseMarginWithFgtsCoverageValidation(formValues)) {
    return true;
  }

  if (!financedAmount) {
    return true;
  }

  return isExactFinancedCoverageAmountValid(
    getMarginWithFgtsCoverageAmount(availableBalance ?? 0, formValues),
    financedAmount,
  );
};

export type TGuaranteeFinancedValidationParams = {
  financedAmount?: number;
  paymentAmount?: number;
  formValues: TLoanGuaranteeFormValues;
  organType?: ConsigneeOrganType;
  isFgtsQueryEnabled?: boolean;
  availableBalance?: number;
};

export type TGuaranteeFinancedValidationResult = {
  isMarginRatioValid: boolean;
  isFgtsGuaranteeSumValid: boolean;
};

export const isConsignedMarginRatioValid = (
  params: TGuaranteeFinancedValidationParams,
): boolean => {
  const {
    financedAmount,
    paymentAmount = 0,
    formValues,
    organType,
    isFgtsQueryEnabled = false,
    availableBalance,
  } = params;

  if (organType !== ConsigneeOrganType.CT || !isFgtsQueryEnabled) {
    return true;
  }

  if (!financedAmount) {
    return true;
  }

  if (shouldUseMarginWithFgtsCoverageValidation(formValues)) {
    return isRescissionMarginWithFgtsCoverageValid(
      availableBalance,
      formValues,
      financedAmount,
    );
  }

  if (!formValues.hasRescissionPenalty) {
    return true;
  }

  if (!availableBalance) {
    return true;
  }

  return paymentAmount / financedAmount >= MARGIN_MIN_LOAN_RATIO;
};

export const getGuaranteeFinancedValidation = (
  params: TGuaranteeFinancedValidationParams,
): TGuaranteeFinancedValidationResult | null => {
  if (!params.financedAmount) {
    return null;
  }

  const isMarginRatioValid = isConsignedMarginRatioValid(params);
  const isFgtsGuaranteeSumValid = isFgtsGuaranteeFinancedRatioValid(
    params.formValues,
    params.financedAmount,
    params.isFgtsQueryEnabled,
    params.availableBalance,
  );

  if (shouldUseMarginWithFgtsCoverageValidation(params.formValues)) {
    return {
      isMarginRatioValid,
      isFgtsGuaranteeSumValid: isMarginRatioValid,
    };
  }

  return {
    isMarginRatioValid,
    isFgtsGuaranteeSumValid,
  };
};
